// @ts-strict-ignore
import { arrayMove } from 'react-sortable-hoc';

import { complement, identity } from './function';
import { isEmpty } from './poly';

// arrToObject([['a', 1], ['b', 2]]) => {a: 1, b: 2}
// Because of a flow computed property issue, we use
// only string for the index: https://github.com/facebook/flow/issues/4310
// It does the same thing as Object.fromEntries I think.
export const arrToObject = <T>(arr: Array<[string, T]>) => {
  const objs = arr.map(([k, v]: [string, T]) => ({
    [k]: v,
  }));
  return Object.assign({}, ...objs);
};

// Create a comparator over T with the transformation f
// E.g., for string s, toComparator(s => s.length)('hey', 'heyo') ==> -1
const toComparator =
  <T, U extends string | number | Date>(
    f: (arg0: T) => U
  ): ((arg0: T, arg1: T) => number) =>
  (a, b) => {
    const fa = f(a);
    const fb = f(b);
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    return +fa < +fb ? -1 : +fa > +fb ? 1 : 0;
  };

// Sort an array based on optional comparator function `fn` without modifying
// original array.
// If `fn` not provided, uses "natural" sort (as in Array.prototype.sort)
export const sort = <T>(
  arr: Array<T>,
  fn?: (arg0: T, arg1: T) => number
): Array<T> => arr.slice().sort(fn);
// Returns a new array sorted by the iteratee function
// E.g., sortBy(['ab', 'a', 'abc'], s => s.length) ==> ['a', 'ab', 'abc']
export const sortBy = <T>(
  a: ReadonlyArray<T>,
  iteratee: (arg0: T) => string | number | Date,
  reverse = false
): Array<T> => {
  const l = a.slice().sort(toComparator(iteratee));
  return reverse ? l.reverse() : l;
};
// Sum an array of numbers
export const sum = (x: Array<number>) => x.reduce((acc, val) => acc + val, 0);
// Returns all items in `a` that are not in `b`.
// Optional transformation tx over which to check equality
// difference([{id: 1, v: 2}, {id: 2, v: 3}], [{id: 1, v: 4}], x => x.id)
// => [{id: 2, v: 3}]
export const difference = <T>(
  a: Array<T>,
  b: Array<T>,
  tx: (arg0: T) => unknown = identity
): Array<T> => a.filter((i) => !b.map(tx).includes(tx(i)));
export const intersection = <T>(
  a: Array<T>,
  b: Array<T>,
  tx: (arg0: T) => unknown = identity
): Array<T> => a.filter((i) => b.map(tx).includes(tx(i)));
export const setEquals = <T>(a: Array<T>, b: Array<T>) =>
  a.length === b.length && a.every((i) => b.includes(i));
export const shiftArrayItemUp = <T>(items: Array<T>, index: number): Array<T> =>
  items
    .slice(0, index - 1)
    .concat([items[index], items[index - 1]])
    .concat(items.slice(index + 1));
export const shiftArrayItemDown = <T>(
  items: Array<T>,
  index: number
): Array<T> => shiftArrayItemUp(items, index + 1);
export const removeArrayIndex = <T>(items: Array<T>, index: number): Array<T> =>
  items.slice(0, index).concat(items.slice(index + 1));
export const updateArrayItem = <T>(
  a: Array<T>,
  index: number,
  newValue: T
): Array<T> =>
  a
    .slice(0, index)
    .concat([newValue])
    .concat(a.slice(index + 1));
// Takes arguments `a`, `oldIndex`, `newIndex`, moving
// item from `oldIndex` to `newIndex`. Does NOT mutate the array!!
// Note: using third-party implementation for now, can replace
// if ever needed
export const moveArrayItem = arrayMove;
// Like `Array.prototype.filter`, but with the opposite style predicate
export const remove = <T>(items: Array<T>, f: (arg0: T) => boolean): Array<T> =>
  items.filter(complement(f));
// Flatten a list of lists into a single list. Single-level flattening
// [[a, b], [c, d]] => [a, b, c, d]
export const flatten = <T>(args: Array<Array<T>>): Array<T> =>
  isEmpty(args) ? [] : Array.prototype.concat.apply(args[0], args.slice(1));
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deepFlatten = (a: Array<any>): Array<any> =>
  a.reduce((acc, e) => acc.concat(Array.isArray(e) ? deepFlatten(e) : e), []);

/*
  `zip` in the style of Python
  zip([1, 2, 3], [4, 5, 6]) => [[1, 4], [2, 5], [3, 6]]
*/
export const zip = <S, T>(
  a: ReadonlyArray<S>,
  b: ReadonlyArray<T>
): Array<[S, T]> => a.map((x, i) => [x, b[i]]);
// Unique an array over the function `uniqueFn`
// `uniqueFn` defaults to `identity`
// E.g.,
// unique([1, 2, 3, 4, 1]) => [1, 2, 3, 4]
// unique([{id: 1}, {id: 2}, {id: 1}], i => i.id) => [{id: 1}, {id: 2}]
export const unique = <T>(
  a: Array<T>,
  uniqueFn: (arg0: T) => unknown = identity
): Array<T> => {
  const seen = new Set();
  return a.reduce((acc, next) => {
    const nextAcc = seen.has(uniqueFn(next)) ? acc : acc.concat([next]);
    seen.add(uniqueFn(next));
    return nextAcc;
  }, []);
};
export const shuffleArray = <T>(array: Array<T>): Array<T> => {
  let currentIndex = array.length;
  let temporaryValue;
  let randomIndex;
  const newArray = [...array];

  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;
    temporaryValue = newArray[currentIndex];
    newArray[currentIndex] = newArray[randomIndex];
    newArray[randomIndex] = temporaryValue;
  }

  return newArray;
};
export const removeId = (
  a: ReadonlyArray<{
    id: string;
  }>,
  idToRemove: string
): ReadonlyArray<{
  id: string;
}> => (a ? a.filter((x) => x.id !== idToRemove) : []);
